import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PDialogFooterBtnActionType, PDialogFooterBtnConfig, PDialogService } from '@frontend-site/ui/components';
import { MfaMethod, UserMfaState } from '@frontend-site/users/domain';
import { IconName } from '@panaya/ui/p-icon';
import { take } from 'rxjs';
import { AuthenticatorSetupDialogComponent } from '../authenticator-setup-dialog/authenticator-setup-dialog.component';
import { UserSecurityTabComponent } from '../security-tab/user-security-tab.component';

export interface AddVerificationMethodDialogData {
    companyId?: number;
    enrolledMethods: MfaMethod[];
}

interface MethodOption {
    method: MfaMethod;
    icon: IconName;
    key: string;
}

/**
 * "Add verification method": the radio-menu of methods the user has not enrolled yet.
 * Picking one opens that method's setup dialog and, on success, closes with the refreshed MFA state.
 */
@Component({
    selector: 'p-add-verification-method-dialog',
    templateUrl: './add-verification-method-dialog.component.html',
    styleUrls: ['./add-verification-method-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddVerificationMethodDialogComponent {
    private static readonly OPTIONS: readonly MethodOption[] = [
        { method: MfaMethod.APP, icon: 'smartphone', key: 'APP' },
        { method: MfaMethod.PASSKEY, icon: 'key', key: 'PASSKEY' }
    ];

    readonly options: MethodOption[];

    constructor(
        private readonly dialogRef: MatDialogRef<AddVerificationMethodDialogComponent, UserMfaState | undefined>,
        @Inject(MAT_DIALOG_DATA) private readonly data: AddVerificationMethodDialogData,
        private readonly dialog: PDialogService
    ) {
        this.options = AddVerificationMethodDialogComponent.OPTIONS.filter(
            (o) => UserSecurityTabComponent.ENROLLABLE_METHODS.includes(o.method) && !data.enrolledMethods.includes(o.method)
        );
    }

    get footerConfig(): PDialogFooterBtnConfig[] {
        return [{ type: PDialogFooterBtnActionType.Close }];
    }

    select(method: MfaMethod): void {
        if (method !== MfaMethod.APP) {
            return;
        }
        this.dialog
            .open(AuthenticatorSetupDialogComponent, { panelClass: 'p-dialog-small', data: { companyId: this.data.companyId } })
            .afterClosed()
            .pipe(take(1))
            .subscribe((state?: UserMfaState) => {
                if (state) {
                    this.dialogRef.close(state);
                }
            });
    }
}
